import { Spin } from "antd";
import { type FC, useEffect, useMemo, useRef, useState } from "react";

import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { isTheSameByDeepCompare } from "../../../../core/utils/utilFunctions";
import type { ExtendedColumnsType } from "../../../index";
import { displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { SelectableTable } from "../../../Table/components/SelectableTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLoadTerminalFromSiteIdsQuery } from "./serverApi";
import type { LoadTerminalsFromSiteIdsRequest } from "./types";

const getColumnsConfig = (t: Function): ExtendedColumnsType<TerminalToCampaignEntity>[] => {
    return [
        {
            title: t("terminal.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("terminal.terminalId"),
            key: "terminalId",
            dataIndex: "terminalId",
        },
        {
            title: t("terminal.terminalType"),
            key: "terminalType",
            dataIndex: "terminalType",
            render: (cellValue) => displayEnumValue(cellValue, "terminal-type"),
        },
    ];
};

export interface CampaignTerminalsSelectableTableProps extends AntCustomFormField<string[]>, CommonFieldAttributes {
    siteIds: string[];
}

export const CampaignTerminalsSelectableTable: FC<CampaignTerminalsSelectableTableProps> = ({ value, onChange, siteIds }) => {
    const [terminalList, setTerminalList] = useState<TerminalEntity[]>([]);

    // Track terminals the user has *explicitly unchecked*.
    // New terminals (from newly added sites) are not in this set and therefore auto-selected.
    // When a site is removed its terminals are removed from this set, so re-adding the site
    // will treat all those terminals as new (auto-selected).
    const [uncheckedIds, setUncheckedIds] = useState<Set<string>>(new Set());

    // True once terminalList has loaded and uncheckedIds has been seeded from `value`.
    const [isInitialized, setIsInitialized] = useState(false);

    // Dedup guard – prevents re-emitting the same selection while waiting for `value` to echo back.
    const lastEmittedRef = useRef<string[] | null>(null);

    // ── fetch available terminalList ────────────────────────────────────────────────────────
    const siteIdsKey = useMemo(() => (siteIds ?? []).join(","), [siteIds]);

    const queryConfig = useMemo<LoadTerminalsFromSiteIdsRequest>(() => {
        const dataGroup = getAssignedDataGroup();
        if (!siteIdsKey || !dataGroup) return {};
        return { siteIds: siteIdsKey, dataGroupCode: dataGroup.code };
    }, [siteIdsKey]);

    const { data, isFetching } = useLoadTerminalFromSiteIdsQuery(queryConfig, { skip: !queryConfig.siteIds });

    useEffect(() => {
        if (data?.terminals) {
            // Fixes AP3ADMIN-2586: strip name / status
            const mapped = data.terminals.map(({ name: _n, status: _s, ...rest }: TerminalEntity) => rest);
            setTerminalList(mapped);
        } else if (!queryConfig.siteIds) {
            setTerminalList([]);
        }
    }, [data, queryConfig.siteIds]);

    // ── initialisation ────────────────────────────────────────────────────────
    // Runs once when terminals first arrive. Seeds uncheckedIds from the
    // terminals that are NOT in the initial `value`.

    useEffect(() => {
        if (isInitialized || terminalList.length === 0) return;
        const incomingValue = value ?? [];
        // On Update (existing value): mark terminals NOT in value as unchecked.
        // On Create (no value): leave uncheckedIds empty so all terminals are auto-selected.
        const newUnchecked =
            incomingValue.length > 0
                ? new Set(terminalList.map((t) => String(t.terminalId)).filter((id) => !new Set(incomingValue).has(id)))
                : new Set<string>();
        setUncheckedIds(newUnchecked);
        setIsInitialized(true);
    }, [isInitialized, terminalList, value]);

    // ── cleanup when terminals are removed ────────────────────────────────────
    // When a site is removed its terminals leave terminalList. We also evict
    // them from uncheckedIds so that if the site is re-added later those
    // terminals are treated as brand-new (auto-selected).

    useEffect(() => {
        if (!isInitialized) return;
        const availableIds = new Set(terminalList.map((t) => String(t.terminalId)));
        setUncheckedIds((prev) => {
            const cleaned = new Set([...prev].filter((id) => availableIds.has(id)));
            return cleaned.size === prev.size ? prev : cleaned;
        });
    }, [isInitialized, terminalList]);

    // ── derived selection ─────────────────────────────────────────────────────

    const selectedTerminalIds = useMemo<string[]>(() => {
        if (!isInitialized) {
            // Before first load, echo the form value so tableData is correct.
            return value ?? [];
        }
        return terminalList.map((t) => String(t.terminalId)).filter((id) => !uncheckedIds.has(id));
    }, [isInitialized, terminalList, uncheckedIds, value]);

    // ── onChange emission ─────────────────────────────────────────────────────
    // Fires whenever the derived selection diverges from the form value.
    // The lastEmittedRef guard prevents re-emitting while waiting for the
    // parent to echo `value` back.

    useEffect(() => {
        if (!onChange || !isInitialized) return;
        const incomingValue = value ?? [];

        // Selection matches what the form already holds – nothing to do.
        if (
            isTheSameByDeepCompare(
                [...incomingValue].sort((a, b) => a.localeCompare(b)),
                [...selectedTerminalIds].sort((a, b) => a.localeCompare(b)),
            )
        )
            return;

        // Already emitted this exact selection; wait for parent to catch up.
        if (
            lastEmittedRef.current &&
            isTheSameByDeepCompare(
                [...lastEmittedRef.current].sort((a, b) => a.localeCompare(b)),
                [...selectedTerminalIds].sort((a, b) => a.localeCompare(b)),
            )
        )
            return;

        lastEmittedRef.current = selectedTerminalIds;
        onChange(selectedTerminalIds);
    }, [isInitialized, onChange, selectedTerminalIds, value]);

    // ── user interaction ──────────────────────────────────────────────────────

    const handleSelectionChange = (selectedItems: TerminalToCampaignEntity[]) => {
        const selectedSet = new Set(selectedItems.map((item) => String(item.terminalId)));
        setUncheckedIds(new Set(terminalList.map((t) => String(t.terminalId)).filter((id) => !selectedSet.has(id))));
    };

    // ── render ────────────────────────────────────────────────────────────────

    const tableData = useMemo(
        () => terminalList.filter((item) => selectedTerminalIds.includes(String(item.terminalId))),
        [terminalList, selectedTerminalIds],
    );

    return (
        <Spin spinning={isFetching}>
            <SelectableTable<TerminalToCampaignEntity>
                name="campaignTerminalsSelectable_table"
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="id"
                dataSource={terminalList}
                tableData={tableData}
                onDataChanged={handleSelectionChange}
            />
        </Spin>
    );
};
